import { ToolbarMaterialTabs } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { FrostedBackdropTarget } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import { LevelSurfacesLoadingPage } from "@-/dev.improve.haniverse.routes.nav.app._components.level.surface";
import { PrimaryControlsStripBack } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import { PrimaryControlsStripTitle } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.title";
import { BrowseLevelsPrimaryControlsStripExpansionContent } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._components.primary-controls-strip-expansion-content";
import { BrowseLevelsToolbar } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._components.toolbar";
import { LevelsPackSheetLayout } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._components.pack-sheet";
import {
  BrowseLevelsLayoutContextBridge,
  BrowseLevelsLayoutContext,
} from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels._context";
import { SuspensePageBoundary } from "@-/dev.improve.haniverse.components.ui.suspense-page";
import { BrowseLevelsKeepAliveSlot } from "#browse-levels-keep-alive-slot";
import { BrowseLevelsComingSoonCurriculumNotice } from "#browse-levels-coming-soon-curriculum-notice";
import { View } from "@-/com.npmjs.react-native";
import { SpaceBackdrop } from "@-/dev.improve.haniverse.components.ui.space-backdrop";
import {
  CustomLevelsPage,
  useCustomLevelsContext,
} from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels.custom";

import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";

export function BrowseLevelsLayoutContent() {
  const { isCustom } = useCustomLevelsContext();
  const [curriculumGrade, gradeOptions, setCurriculumGrade] = useContextSelectors(
    BrowseLevelsLayoutContext,
    (c) => c.curriculumGrade,
    (c) => c.gradeOptions,
    (c) => c.setCurriculumGrade,
  );
  return (
    <>
      <PrimaryControlsStripBack hidden />
      {!isCustom ? (
        <BrowseLevelsLayoutContextBridge>
          <BrowseLevelsPrimaryControlsStripExpansionContent />
        </BrowseLevelsLayoutContextBridge>
      ) : null}
      <PrimaryControlsStripTitle>Levels</PrimaryControlsStripTitle>
      <View className="min-h-0 flex-1">
        {/* Experimental: the sky behind the level list, between and around its white surfaces. */}
        <SpaceBackdrop />
        {isCustom ? (
          <>
            <BrowseLevelsToolbar />
            <BrowseLevelsListViewport isCustom />
          </>
        ) : (
          // Web uses a right sidebar; native keeps the collapsible top sheet.
          <LevelsPackSheetLayout
            renderTabs={({ openSheet, sheetGrabber }) => (
              // Opaque grade tabs under the bar (same pattern as Characters/Words HSK): material-tab
              // labels stay legible, so this page opts out of the iOS 26 glass see-through header.
              <ToolbarMaterialTabs
                // Fades in beneath the selected pack's label while its sheet is collapsed.
                activeTabAccessory={sheetGrabber}
                // Pressing the selected pack again expands its collapsed sheet.
                onActiveTabPress={openSheet}
                // Packs past the free tier open without a paywall: blurred but scrollable, under the
                // pack's description and the list's own upgrade prompt, so the learner sees what's
                // behind the lock.
                onValueChange={setCurriculumGrade}
                options={gradeOptions}
                value={curriculumGrade}
              />
            )}
            // Reduce the pill toolbar's bottom gutter before the material tab strip.
            tabsClassName="-mt-2"
            toolbar={<BrowseLevelsToolbar />}
          >
            <BrowseLevelsListViewport isCustom={false} />
          </LevelsPackSheetLayout>
        )}
      </View>
    </>
  );
}

function BrowseLevelsListViewport({ isCustom }: { isCustom: boolean }) {
  const [browsedHanziScript, isBrowsingStudiedCurriculum] = useContextSelectors(
    BrowseLevelsLayoutContext,
    (c) => c.browsedHanziScript,
    (c) => c.isBrowsingStudiedCurriculum,
  );
  return (
    // Page-scoped boundary for the completion sub-routes. Without one, a failed catalog read (the
    // batched filter-facts query throws on error, so a stale-schema worker 500 is surfaced rather
    // than rendered as a silently unfiltered list) would climb to the app-shell boundary and
    // replace the whole app — header, tabs and all — with the error screen. Here it replaces only
    // the list body, under the same header and toolbar, so the rest of Han stays usable.
    <View className="min-h-0 flex-1">
      <FrostedBackdropTarget className="min-h-0 flex-1">
        {/* Expo captures the inner target's children, not its outer view background. */}
        <View className="min-h-0 flex-1 bg-background">
          <SuspensePageBoundary
            fallback={<LevelSurfacesLoadingPage />}
            location={import.meta.location}
          >
            {isCustom ? <CustomLevelsPage /> : <BrowseLevelsKeepAliveSlot />}
          </SuspensePageBoundary>
        </View>
      </FrostedBackdropTarget>
      {/* Outside the captured list to avoid an Android blur-target cycle. Native tabs are above
          this scene; the toolbar and material tabs are above this viewport. */}
      {!isCustom && (browsedHanziScript === "traditional" || !isBrowsingStudiedCurriculum) ? (
        <BrowseLevelsComingSoonCurriculumNotice
          label={`${browsedHanziScript === "traditional" ? "Traditional" : "Simplified"} Curriculum`}
        />
      ) : null}
    </View>
  );
}
